import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

type SettingsData = {
  id: number;
  zapier_webhook_url: string | null;
  ga4_measurement_id: string | null;
  gads_conversion_snippet: string | null;
  meta_pixel_id: string | null;
  elfsight_instagram_embed: string | null;
};

const Settings = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<SettingsData>({
    id: 1,
    zapier_webhook_url: null,
    ga4_measurement_id: null,
    gads_conversion_snippet: null,
    meta_pixel_id: null,
    elfsight_instagram_embed: null,
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from("settings")
        .select("*")
        .eq("id", 1)
        .maybeSingle();

      if (error) throw error;
      
      if (data) {
        setFormData(data);
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to fetch settings",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("settings")
        .upsert(formData, { onConflict: "id" });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Settings saved successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to save settings",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/admin/dashboard")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold text-foreground">General Settings</h1>
          </div>
          <Button onClick={handleSubmit} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {loading ? (
          <Card>
            <CardContent className="text-center py-8 text-muted-foreground">
              Loading...
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Integrations */}
            <Card>
              <CardHeader>
                <CardTitle>Integrations</CardTitle>
                <CardDescription>
                  Configure third-party service integrations
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="zapier_webhook_url">Zapier Webhook URL</Label>
                  <Input
                    id="zapier_webhook_url"
                    value={formData.zapier_webhook_url || ""}
                    onChange={(e) => setFormData({ ...formData, zapier_webhook_url: e.target.value })}
                    placeholder="https://hooks.zapier.com/hooks/catch/..."
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Lead submissions will be sent to this webhook URL
                  </p>
                </div>

                <div>
                  <Label htmlFor="elfsight_instagram_embed">Elfsight Instagram Embed Code</Label>
                  <Textarea
                    id="elfsight_instagram_embed"
                    value={formData.elfsight_instagram_embed || ""}
                    onChange={(e) => setFormData({ ...formData, elfsight_instagram_embed: e.target.value })}
                    placeholder='<script src="https://apps.elfsight.com/...">'
                    rows={3}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Paste your Elfsight Instagram feed embed code
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Analytics & Tracking */}
            <Card>
              <CardHeader>
                <CardTitle>Analytics & Tracking</CardTitle>
                <CardDescription>
                  Configure analytics and conversion tracking
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="ga4_measurement_id">Google Analytics 4 Measurement ID</Label>
                  <Input
                    id="ga4_measurement_id"
                    value={formData.ga4_measurement_id || ""}
                    onChange={(e) => setFormData({ ...formData, ga4_measurement_id: e.target.value })}
                    placeholder="G-XXXXXXXXXX"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Example: G-XXXXXXXXXX
                  </p>
                </div>

                <div>
                  <Label htmlFor="meta_pixel_id">Meta (Facebook) Pixel ID</Label>
                  <Input
                    id="meta_pixel_id"
                    value={formData.meta_pixel_id || ""}
                    onChange={(e) => setFormData({ ...formData, meta_pixel_id: e.target.value })}
                    placeholder="123456789012345"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Your Facebook Pixel ID (15-16 digit number)
                  </p>
                </div>

                <div>
                  <Label htmlFor="gads_conversion_snippet">Google Ads Conversion Snippet</Label>
                  <Textarea
                    id="gads_conversion_snippet"
                    value={formData.gads_conversion_snippet || ""}
                    onChange={(e) => setFormData({ ...formData, gads_conversion_snippet: e.target.value })}
                    placeholder="<!-- Google tag (gtag.js) -->..."
                    rows={6}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Paste your complete Google Ads conversion tracking snippet
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Important Notes */}
            <Card className="border-azure-500">
              <CardHeader>
                <CardTitle className="text-azure-500">Important Notes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p>
                  <strong>Privacy:</strong> Make sure you have appropriate privacy policies in place when using tracking pixels and analytics.
                </p>
                <p>
                  <strong>Testing:</strong> After saving changes, test all integrations to ensure they're working correctly.
                </p>
                <p>
                  <strong>GDPR/CCPA:</strong> If you operate in regions with privacy regulations, ensure you have proper consent mechanisms.
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default Settings;
